<template>
  <section v-if="updateAvailable" class="app-update-banner" role="status" aria-live="polite">
    <span>{{ language.t('app.updateAvailable') }}</span>
    <button type="button" @click="refreshApp">{{ language.t('app.refreshNow') }}</button>
  </section>
  <LoginView v-if="!session.isAuthenticated" />
  <ChatShell v-else />
  <LanguageSwitcher v-if="session.isAuthenticated" />
  <VoiceCallDialog v-if="session.isAuthenticated" />
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import LoginView from './components/LoginView.vue';
import ChatShell from './components/ChatShell.vue';
import LanguageSwitcher from './components/LanguageSwitcher.vue';
import VoiceCallDialog from './components/VoiceCallDialog.vue';
import { useSessionStore } from './stores/session';
import { useConversationStore } from './stores/conversations';
import { useLanguageStore } from './stores/language';
import { hasNewAppVersion } from './utils/appVersion.mjs';
import { appConfig } from './config';
import { loadProjectBrand } from './services/projectBrand';
import { restorePushSession } from './services/webPush';
import { restoreNativePushSession } from './services/nativePush';
import { useNavigationStore } from './stores/navigation';

const session = useSessionStore();
const conversations = useConversationStore();
const language = useLanguageStore();
const navigation = useNavigationStore();
let pendingPush = null;
function openPushConversation(data) {
  if (data?.user_id !== session.userId) return;
  pendingPush = data.conversation_id;
  applyPushConversation();
}
function applyPushConversation() {
  if (!pendingPush || !session.isAuthenticated || !conversations.conversations.some(row => row.id === pendingPush)) return;
  navigation.showMessages();
  conversations.setActiveConversation(pendingPush);
  pendingPush = null;
  const url = new URL(location.href);url.searchParams.delete('pushUser');url.searchParams.delete('pushConversation');history.replaceState(null,'',url.href);
}
function pushMessage(event) { if (event.data?.type === 'JC_PUSH_OPEN') openPushConversation(event.data); }
watch(() => [session.userId,session.token], ([userId,token]) => {
  restorePushSession({userId,token}).catch(() => {});
  restoreNativePushSession({userId,token}).catch(() => {});
  const url=new URL(location.href);if(url.searchParams.get('pushUser')===userId)openPushConversation({user_id:userId,conversation_id:url.searchParams.get('pushConversation')});
}, {immediate:true});
watch(() => conversations.conversations, applyPushConversation, {deep:false});
const updateAvailable = ref(false);
let updateCheckTimer = null;
let brandRefreshTimer = null;

function currentAppAssetUrl() {
  return Array.from(document.scripts)
    .map(script => script.src)
    .find(source => /\/js\/app\.[^/]+\.js(?:[?#]|$)/i.test(source)) || '';
}

async function checkForAppUpdate() {
  if (appConfig.capacitorApp || updateAvailable.value) {
    return;
  }
  try {
    const url = new URL('index.html', document.baseURI);
    url.searchParams.set('update_check', Date.now().toString());
    const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
    if (response.ok) {
      updateAvailable.value = hasNewAppVersion(currentAppAssetUrl(), await response.text());
    }
  } catch {
    // A failed version check must not interrupt login or messaging.
  }
}

function checkWhenVisible() {
  if (document.visibilityState === 'visible') {
    checkForAppUpdate();
  }
}

function refreshApp() {
  window.location.reload();
}

onMounted(() => {
  navigator.serviceWorker?.addEventListener('message',pushMessage);
  language.restore();
  session.restore();
  loadProjectBrand();
  brandRefreshTimer = window.setInterval(loadProjectBrand, 60000);
  window.addEventListener('focus', loadProjectBrand);
  checkForAppUpdate();
  updateCheckTimer = window.setInterval(checkForAppUpdate, 60 * 1000);
  window.addEventListener('focus', checkForAppUpdate);
  document.addEventListener('visibilitychange', checkWhenVisible);
});

onBeforeUnmount(() => {
  navigator.serviceWorker?.removeEventListener('message',pushMessage);
  window.clearInterval(brandRefreshTimer);
  window.removeEventListener('focus', loadProjectBrand);
  if (updateCheckTimer) {
    window.clearInterval(updateCheckTimer);
  }
  window.removeEventListener('focus', checkForAppUpdate);
  document.removeEventListener('visibilitychange', checkWhenVisible);
});

watch(() => session.userId, userId => {
  if (userId && session.isAuthenticated) {
    conversations.bootstrapDemoData();
    return;
  }
  conversations.clearUserState();
});
</script>
